import React, { useState, useEffect, useCallback } from 'react';
import { Plus, QrCode, Copy, Check, Trash2, Play, Pause, X, Users, BarChart3, RefreshCw, CheckCircle, XCircle, Wand2, Clock, CreditCard, ChevronDown, ChevronUp, Mail } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getCsrfToken = () => {
  const match = document.cookie.match(/(?:^|;\s*)tcl_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

/** Tiny SVG sparkline — gold line, 80×24 */
const Sparkline = ({ data }) => {
  if (!data || data.length < 2) return null;
  const W = 80, H = 22;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * W;
    const y = H - (v / max) * H;
    return x + ',' + y;
  }).join(' ');
  const area = 'M ' + data.map((v, i) => {
    const x = (i / (data.length - 1)) * W;
    const y = H - (v / max) * H;
    return x + ' ' + y;
  }).join(' L ') + ' L ' + W + ' ' + H + ' L 0 ' + H + ' Z';
  return (
    <svg width={W} height={H} className="overflow-visible" aria-hidden="true">
      <defs>
        <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C99316" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#C99316" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#sg)" />
      <polyline points={pts} fill="none" stroke="#C99316" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-600',
  live: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-red-100 text-red-600',
  pending_approval: 'bg-amber-100 text-amber-700',
  rejected: 'bg-red-50 text-red-400',
};

const generateCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return 'CFQ-' + Array.from({ length: 5 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const AdminSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', speaker_email: '' });
  const [copied, setCopied] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [paymentSettings, setPaymentSettings] = useState({ enabled: false, price: 0.0, currency: 'GBP' });
  const [showPayment, setShowPayment] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [emailPreview, setEmailPreview] = useState({ show: false, html: '', loading: false, sessionName: '' });
  const [rejectModal, setRejectModal] = useState({ show: false, id: null, name: '', reason: '' });
  const [trends, setTrends] = useState({});
  const { token } = useAuth();

  const authHeaders = useCallback(() => {
    const csrf = getCsrfToken();
    const h = { 'Content-Type': 'application/json', credentials: 'include' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    if (csrf) h['X-CSRF-Token'] = csrf;
    return h;
  }, [token]);

  const fetchSessions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/sessions/list`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const all = data.sessions || [];
        setPending(all.filter(s => s.status === 'pending_approval'));
        setSessions(all.filter(s => s.status !== 'pending_approval'));
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, authHeaders]);

  const fetchPaymentSettings = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/sessions/admin/payment-settings`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      if (res.ok) setPaymentSettings(await res.json());
    } catch {}
  }, [token, authHeaders]);

  const fetchTrends = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/sessions/admin/trends`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setTrends(data.trends || {});
      }
    } catch {}
  }, [token, authHeaders]);

  useEffect(() => {
    fetchSessions();
    fetchPaymentSettings();
    fetchTrends();
  }, [fetchSessions, fetchPaymentSettings, fetchTrends]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/sessions/create`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowCreate(false);
        setForm({ name: '', code: '', speaker_email: '' });
        fetchSessions();
        toast.success('Session created');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create session');
      }
    } catch (e) { toast.error('Network error'); }
  };

  const setLoading_ = (id, val) => setActionLoading(p => ({ ...p, [id]: val }));

  const handlePreviewEmail = async (id, name) => {
    setEmailPreview({ show: true, html: '', loading: true, sessionName: name });
    try {
      const res = await fetch(`${API_URL}/api/sessions/admin/approval-email-preview/${id}`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      if (res.ok) {
        const html = await res.text();
        setEmailPreview({ show: true, html, loading: false, sessionName: name });
      } else {
        toast.error('Could not load email preview');
        setEmailPreview(p => ({ ...p, show: false }));
      }
    } catch {
      toast.error('Network error loading preview');
      setEmailPreview(p => ({ ...p, show: false }));
    }
  };

  const handleApprove = async (id) => {
    setLoading_(id, 'approve');
    try {
      const res = await fetch(`${API_URL}/api/sessions/admin/approve/${id}`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Session approved — speaker notified by email');
        fetchSessions();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Approval failed');
      }
    } catch { toast.error('Network error'); }
    setLoading_(id, null);
  };

  const handleReject = (id, name) => {
    setRejectModal({ show: true, id, name, reason: '' });
  };

  const confirmReject = async () => {
    const { id, reason } = rejectModal;
    setLoading_(id, 'reject');
    setRejectModal(m => ({ ...m, show: false }));
    try {
      const res = await fetch(`${API_URL}/api/sessions/admin/reject/${id}`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ reason: reason.trim() || null }),
      });
      if (res.ok) {
        toast.success('Request rejected' + (reason.trim() ? ' — speaker notified with your note' : ''));
        fetchSessions();
      }
    } catch {}
    setLoading_(id, null);
  };

  const updateStatus = async (id, status) => {
    setLoading_(id, status);
    try {
      const res = await fetch(`${API_URL}/api/sessions/update/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ status }),
      });
      if (res.ok) fetchSessions();
      else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Update failed');
      }
    } catch {}
    setLoading_(id, null);
  };

  const deleteSession = async (id) => {
    if (!window.confirm('Delete this session permanently?')) return;
    try {
      const res = await fetch(`${API_URL}/api/sessions/delete/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
        credentials: 'include',
      });
      if (res.ok) { fetchSessions(); toast.success('Session deleted'); }
    } catch {}
  };

  const copyUrl = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/cfq-assessment?session=${code}`);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  const savePaymentSettings = async () => {
    setSavingPayment(true);
    try {
      const res = await fetch(`${API_URL}/api/sessions/admin/payment-settings`, {
        method: 'PATCH',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify(paymentSettings),
      });
      if (res.ok) toast.success('Payment settings saved');
      else toast.error('Failed to save');
    } catch { toast.error('Network error'); }
    setSavingPayment(false);
  };

  if (loading) return <div className="py-16 text-center text-cream/20">Loading...</div>;

  return (
    <div data-testid="admin-sessions">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">Sessions</h1>
          <p className="text-cream/30 text-sm mt-1">
            {pending.length > 0 && <span className="text-amber-400">{pending.length} pending approval · </span>}
            Manage live CFQ sessions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchSessions} className="p-2 text-cream/30 hover:text-cream/60" title="Refresh"><RefreshCw size={15} /></button>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gold text-navy text-sm font-medium rounded-lg hover:bg-gold/90"
            data-testid="create-session-btn"
          >
            <Plus size={16} /> Create Session
          </button>
        </div>
      </div>

      {/* Pending Approvals */}
      {pending.length > 0 && (
        <div className="mb-6 border border-amber-500/20 rounded-xl overflow-hidden">
          <div className="bg-amber-500/10 px-5 py-3 flex items-center gap-2">
            <Clock size={14} className="text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">Pending Approval ({pending.length})</span>
          </div>
          <div className="divide-y divide-amber-500/10">
            {pending.map(s => (
              <div key={s.id} className="px-5 py-4 flex items-center justify-between gap-4" data-testid={`pending-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-cream font-medium truncate">{s.name}</p>
                  <p className="text-cream/30 text-xs font-mono mt-0.5">
                    {s.speaker_email} · Requested {new Date(s.created_at).toLocaleDateString()}
                    {s.start_date && ` · Event: ${s.start_date}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handlePreviewEmail(s.id, s.name)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-cream/10 text-cream/30 text-xs rounded-lg hover:border-gold/20 hover:text-gold/60"
                    title="Preview approval email"
                    data-testid={`preview-email-${s.id}`}
                  >
                    <Mail size={12} /> Preview Email
                  </button>
                  <button
                    onClick={() => handleApprove(s.id)}
                    disabled={!!actionLoading[s.id]}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-lg hover:bg-emerald-500/30 disabled:opacity-50"
                    data-testid={`approve-${s.id}`}
                  >
                    <CheckCircle size={13} />
                    {actionLoading[s.id] === 'approve' ? 'Approving…' : 'Approve'}
                  </button>
                  <button
                    onClick={() => handleReject(s.id, s.name)}
                    disabled={!!actionLoading[s.id]}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 text-red-400 text-xs rounded-lg hover:bg-red-500/20 disabled:opacity-50"
                    data-testid={`reject-${s.id}`}
                  >
                    <XCircle size={13} />
                    {actionLoading[s.id] === 'reject' ? 'Rejecting…' : 'Reject'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Email Preview Modal */}
      {emailPreview.show && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-navy border border-gold/10 rounded-xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gold/10 shrink-0">
              <div>
                <h2 className="text-cream text-base font-semibold">Email Preview</h2>
                <p className="text-cream/30 text-xs mt-0.5 truncate max-w-sm">{emailPreview.sessionName}</p>
              </div>
              <button onClick={() => setEmailPreview(p => ({ ...p, show: false }))} className="text-cream/30 hover:text-cream">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              {emailPreview.loading ? (
                <div className="h-64 flex items-center justify-center text-cream/20 text-sm">Loading preview…</div>
              ) : (
                <iframe
                  srcDoc={emailPreview.html}
                  title="Email Preview"
                  className="w-full rounded-lg bg-white"
                  style={{ height: '60vh', border: 'none' }}
                  data-testid="email-preview-iframe"
                />
              )}
            </div>
            <div className="px-5 py-3 border-t border-gold/10 shrink-0">
              <p className="text-cream/20 text-xs">This is exactly what the speaker will receive when approved. The email includes their session management link and participant URL.</p>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {rejectModal.show && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-navy border border-red-500/20 rounded-xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-cream text-base font-semibold">Reject Session Request</h2>
                <p className="text-cream/30 text-xs mt-0.5 truncate max-w-xs">{rejectModal.name}</p>
              </div>
              <button onClick={() => setRejectModal(m => ({ ...m, show: false }))} className="text-cream/30 hover:text-cream mt-0.5">
                <X size={18} />
              </button>
            </div>
            <div className="mb-5">
              <label className="text-cream/40 text-xs block mb-1.5">
                Reason for rejection <span className="text-cream/20">(optional — included in the speaker's email)</span>
              </label>
              <textarea
                value={rejectModal.reason}
                onChange={e => setRejectModal(m => ({ ...m, reason: e.target.value }))}
                placeholder="e.g. Please re-submit with the correct event date format (YYYY-MM-DD)."
                rows={3}
                className="w-full bg-navy-deep border border-cream/10 rounded-lg px-4 py-2.5 text-cream text-sm placeholder-cream/15 focus:outline-none focus:border-red-500/30 resize-none"
                data-testid="rejection-reason-input"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setRejectModal(m => ({ ...m, show: false }))}
                className="flex-1 py-2 border border-cream/10 text-cream/40 text-sm rounded-lg hover:border-cream/20"
                data-testid="rejection-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                className="flex-1 py-2 bg-red-500/20 text-red-400 text-sm rounded-lg hover:bg-red-500/30 font-medium"
                data-testid="rejection-confirm-btn"
              >
                Reject Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-navy border border-gold/10 rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-cream text-lg font-semibold">Create Session</h2>
              <button onClick={() => setShowCreate(false)} className="text-cream/30 hover:text-cream"><X size={18} /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="text-cream/50 text-xs block mb-1">Session Name</label>
                <input
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Las Vegas Leadership Summit"
                  required
                  className="w-full bg-navy-deep border border-cream/10 rounded-lg px-4 py-2.5 text-cream text-sm placeholder-cream/20 focus:outline-none focus:border-gold/30"
                  data-testid="session-name-input"
                />
              </div>
              <div>
                <label className="text-cream/50 text-xs block mb-1">Session Code</label>
                <div className="flex gap-2">
                  <input
                    value={form.code}
                    onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="e.g. CFQ-AB3X7"
                    required
                    className="flex-1 bg-navy-deep border border-cream/10 rounded-lg px-4 py-2.5 text-cream text-sm font-mono placeholder-cream/20 focus:outline-none focus:border-gold/30"
                    data-testid="session-code-input"
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, code: generateCode() })}
                    className="px-3 py-2 border border-gold/20 text-gold/60 text-xs rounded-lg hover:border-gold/40 hover:text-gold flex items-center gap-1"
                    title="Auto-generate code"
                    data-testid="generate-code-btn"
                  >
                    <Wand2 size={13} /> Auto
                  </button>
                </div>
              </div>
              <div>
                <label className="text-cream/50 text-xs block mb-1">Assigned Speaker Email (optional)</label>
                <input
                  value={form.speaker_email}
                  onChange={e => setForm({ ...form, speaker_email: e.target.value })}
                  placeholder="speaker@example.com"
                  type="email"
                  className="w-full bg-navy-deep border border-cream/10 rounded-lg px-4 py-2.5 text-cream text-sm placeholder-cream/20 focus:outline-none focus:border-gold/30"
                  data-testid="session-speaker-input"
                />
              </div>
              <button type="submit" className="w-full bg-gold text-navy py-2.5 rounded-lg text-sm font-medium hover:bg-gold/90" data-testid="session-create-submit">
                Create Session
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Sessions List */}
      {sessions.length === 0 ? (
        <div className="text-center py-20">
          <QrCode size={32} className="text-cream/10 mx-auto mb-3" />
          <p className="text-cream/30 text-sm">No sessions yet. Create one or wait for speaker requests.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map(s => (
            <div key={s.id} className="bg-navy border border-gold/[0.06] rounded-xl p-5" data-testid={`session-${s.code}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-cream font-medium">{s.name}</h3>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[s.status] || STATUS_STYLES.draft}`}>{s.status}</span>
                  </div>
                  <p className="text-cream/30 text-sm font-mono">{s.code}</p>
                </div>
                <div className="flex items-center gap-2">
                  {s.status === 'draft' && (
                    <button onClick={() => updateStatus(s.id, 'live')} disabled={!!actionLoading[s.id]} className="p-2 text-emerald-400 hover:bg-emerald-500/10 rounded-lg disabled:opacity-40" title="Go live">
                      <Play size={16} />
                    </button>
                  )}
                  {s.status === 'live' && (
                    <button onClick={() => updateStatus(s.id, 'closed')} disabled={!!actionLoading[s.id]} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg disabled:opacity-40" title="Close">
                      <Pause size={16} />
                    </button>
                  )}
                  {s.status === 'closed' && (
                    <button onClick={() => updateStatus(s.id, 'live')} disabled={!!actionLoading[s.id]} className="p-2 text-emerald-400 hover:bg-emerald-500/10 rounded-lg disabled:opacity-40" title="Reopen">
                      <Play size={16} />
                    </button>
                  )}
                  <button onClick={() => deleteSession(s.id)} className="p-2 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded-lg" title="Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-4 text-cream/20 text-xs mb-3">
                <span className="flex items-center gap-1"><BarChart3 size={12} /> {s.pulse_count || 0} completions</span>
                <span className="flex items-center gap-1"><Users size={12} /> {s.share_count || 0} shared</span>
                {s.speaker_email && <span>Speaker: {s.speaker_email}</span>}
                {trends[s.code] && (
                  <span className="ml-auto flex items-center gap-2">
                    <Sparkline data={trends[s.code]} />
                    <span className="text-cream/15 text-[10px]">7d</span>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => copyUrl(s.code)} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-cream/10 text-cream/40 text-xs rounded-lg hover:border-gold/20 hover:text-gold" data-testid={`copy-url-${s.code}`}>
                  {copied === s.code ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy URL</>}
                </button>
                <a
                  href={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(`${window.location.origin}/cfq-assessment?session=${s.code}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-cream/10 text-cream/40 text-xs rounded-lg hover:border-gold/20 hover:text-gold"
                >
                  <QrCode size={12} /> QR Code
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Payment Settings */}
      <div className="mt-8 border border-cream/[0.05] rounded-xl overflow-hidden">
        <button
          onClick={() => setShowPayment(p => !p)}
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.02] transition-colors"
          data-testid="payment-settings-toggle"
        >
          <div className="flex items-center gap-2">
            <CreditCard size={15} className="text-cream/30" />
            <span className="text-cream/50 text-sm font-medium">Live Session Payment Settings</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${paymentSettings.enabled ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
              {paymentSettings.enabled ? 'ON' : 'OFF'}
            </span>
          </div>
          {showPayment ? <ChevronUp size={15} className="text-cream/20" /> : <ChevronDown size={15} className="text-cream/20" />}
        </button>
        {showPayment && (
          <div className="px-5 pb-5 pt-1 border-t border-cream/[0.05] space-y-4">
            <p className="text-cream/30 text-xs leading-relaxed">Payment for live sessions is currently disabled. Speakers can request sessions at no cost. Enable this in the future to charge speakers before their session is activated.</p>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <div
                  onClick={() => setPaymentSettings(p => ({ ...p, enabled: !p.enabled }))}
                  className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${paymentSettings.enabled ? 'bg-gold' : 'bg-cream/10'}`}
                  data-testid="payment-enabled-toggle"
                >
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${paymentSettings.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
                <span className="text-cream/50 text-sm">{paymentSettings.enabled ? 'Payments enabled' : 'Payments disabled'}</span>
              </label>
            </div>
            {paymentSettings.enabled && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-navy-deep border border-cream/10 rounded-lg px-3 py-2">
                  <select
                    value={paymentSettings.currency}
                    onChange={e => setPaymentSettings(p => ({ ...p, currency: e.target.value }))}
                    className="bg-transparent text-cream/60 text-sm focus:outline-none"
                    data-testid="payment-currency-select"
                  >
                    <option value="GBP">GBP £</option>
                    <option value="USD">USD $</option>
                    <option value="EUR">EUR €</option>
                  </select>
                </div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentSettings.price}
                  onChange={e => setPaymentSettings(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))}
                  className="w-28 bg-navy-deep border border-cream/10 rounded-lg px-3 py-2 text-cream text-sm focus:outline-none focus:border-gold/30"
                  placeholder="0.00"
                  data-testid="payment-price-input"
                />
              </div>
            )}
            <button
              onClick={savePaymentSettings}
              disabled={savingPayment}
              className="px-4 py-2 bg-gold/20 text-gold text-sm rounded-lg hover:bg-gold/30 disabled:opacity-50"
              data-testid="save-payment-settings"
            >
              {savingPayment ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSessions;
